import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import PreviewTemplate from "./PreviewTemplate";
import { useTranslation } from "react-i18next";

/**
 * Preview Carousel Component used to store logic for navigating Templates slides
 * @param {String} previewType Type of Preview "ALL|CREATE"
 * @param {Array} disruptions Disruptions data Array Object
 */
const PreviewCarousel = ({ previewType, disruptions }) => {
  const { t } = useTranslation("smrtDisruption");
  const [order, setOrder] = useState(0);
  const [type, setType] = useState(
    disruptions[0].type !== "station-closure" ? "DISRUPTION" : "CLOSURE"
  );

  /**
   * Function to show next slide
   * Flow : D[<order>] > (if exist) A[<order>] > D[<order+1>] > ...
   * D = Disruption | A  = Affected
   */
  const nextSlide = () => {
    const nextOrder = () => {
      const isLast = disruptions.length === order + 1;
      if (disruptions[isLast ? 0 : order + 1].type === "station-closure")
        setType("CLOSURE");
      setOrder(isLast ? 0 : order + 1);
    };

    if (type === "DISRUPTION") {
      const current = disruptions[order];
      const noAffectedStations = current.affectedStations.length < 1;
      const isLRT = current.line.code.includes("LRT");
      const isClosure = current.type === "station-closure";

      if (isClosure) {
        setType("CLOSURE");
      } else if (noAffectedStations || isLRT) {
        nextOrder(); // Skip AFFECTED slide
      } else {
        setType("AFFECTED");
      }
    } else if (type === "AFFECTED" || type === "CLOSURE") {
      setType("DISRUPTION");
      nextOrder(); // Go to next disruption
    }
  };

  /**
   * Function to show previous slide
   * Flow : D[<order>] > (if exist) A[<order-1>] > D[<order-1>] > ...
   * D = Disruption | A  = Affected
   */
  const prevSlide = () => {
    const prevOrder = () => {
      const isFirst = order === 0;
      const newOrder = isFirst ? disruptions.length - 1 : order - 1;

      // If the previous disruption is a closure, set type directly
      if (disruptions[newOrder].type === "station-closure") {
        setType("CLOSURE");
      } else {
        setType("DISRUPTION");
      }

      setOrder(newOrder);
    };

    if (type === "DISRUPTION") {
      const current = disruptions[order];
      const noAffectedStations = current.affectedStations.length < 1;
      const isLRT = current.line.code.includes("LRT");
      const isClosure = current.type === "station-closure";

      if (isClosure) {
        setType("CLOSURE");
      } else if (noAffectedStations || isLRT) {
        prevOrder(); // Skip back over AFFECTED
      } else {
        setType("AFFECTED");
      }
    } else if (type === "AFFECTED" || type === "CLOSURE") {
      // Go back to disruption (or previous disruption if closure)
      setType("DISRUPTION");
      prevOrder();
    }
  };

  return (
    <>
      <Box
        //DS Display have aspect ratio of 16:9
        // 1920x1080 resolution
        sx={{ aspectRatio: "16/9", height: "60vh" }}
      >
        <PreviewTemplate disruptions={disruptions} type={type} order={order} />
      </Box>
      <Box sx={{ mt: 2 }} display={"flex"} justifyContent={"center"}>
        {/**
         * PreviewType "ALL" is on Disruption index component
         * This preview will show all of the Disruption and(if exist) Affected
         */}
        {previewType === "ALL" && (
          <>
            <Button
              variant="contained"
              onClick={() => {
                prevSlide();
              }}
            >
              {t("preview.backBtn")}
            </Button>

            <Box sx={{ mx: 2 }}></Box>

            <Button
              variant="contained"
              onClick={() => {
                nextSlide();
              }}
            >
              {t("preview.nextBtn")}
            </Button>
          </>
        )}

        {/**
         * PreviewType "CREATE" is on Create Disruption Component
         * This preview will only show one Disruption and(if exist) Affected
         */}
        {previewType === "CREATE" &&
          (type === "DISRUPTION" ? (
            <Button
              variant="contained"
              onClick={() => {
                setType("AFFECTED");
              }}
              disabled={
                disruptions[order].affectedStations.length < 1 ||
                disruptions[order].line.code.includes("LRT")
              }
            >
              {t("preview.viewAffectedBtn")}
            </Button>
          ) : type === "DISRUPTION" ? (
            <Button
              variant="contained"
              onClick={() => {
                setType("DISRUPTION");
              }}
            >
              {t("preview.viewDisruptionBtn")}
            </Button>
          ) : (
            <></>
          ))}
      </Box>
    </>
  );
};

export default PreviewCarousel;
